import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';

interface QuickActionsProps {
  onSelectTemplate: (template: string) => void;
}

const SvgIcon: React.FC<{ d: string; className?: string }> = ({ d, className = '' }) => (
  <svg className={`w-5 h-5 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

export const QuickActions: React.FC<QuickActionsProps> = ({ onSelectTemplate }) => {
  const { t } = useT();
  const [isExpanded, setIsExpanded] = useState(false);

  const templates = [
    { id: 'learning', icon: <SvgIcon d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />, labelKey: 'quickActions.learning', templateKey: 'quickActions.learningTemplate', descKey: 'quickActions.learningDesc' },
    { id: 'tips', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>, labelKey: 'quickActions.tips', templateKey: 'quickActions.tipsTemplate', descKey: 'quickActions.tipsDesc' },
    { id: 'opinion', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>, labelKey: 'quickActions.opinion', templateKey: 'quickActions.opinionTemplate', descKey: 'quickActions.opinionDesc' },
    { id: 'question', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>, labelKey: 'quickActions.question', templateKey: 'quickActions.questionTemplate', descKey: 'quickActions.questionDesc' },
    { id: 'experience', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>, labelKey: 'quickActions.experience', templateKey: 'quickActions.experienceTemplate', descKey: 'quickActions.experienceDesc' },
    { id: 'thread', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>, labelKey: 'quickActions.thread', templateKey: 'quickActions.threadTemplate', descKey: 'quickActions.threadDesc' },
  ];

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-brand hover:text-brand/80 transition-colors text-sm font-medium"
      >
        <span>{isExpanded ? '▼' : '▶'}</span>
        <span>{t('quickActions.toggle')}</span>
      </button>

      {isExpanded && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {templates.map((tmpl) => (
            <button
              key={tmpl.id}
              onClick={() => {
                onSelectTemplate(t(tmpl.templateKey));
                setIsExpanded(false);
              }}
              className={cn(
                'p-3 bg-surface-2 border border-border rounded-lg',
                'hover:border-brand hover:bg-brand/5 transition-all',
                'text-left'
              )}
            >
              <div className="flex items-start gap-2">
                <span className="text-text-muted flex-shrink-0">{tmpl.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {t(tmpl.labelKey)}
                  </div>
                  <div className="text-xs text-text-muted truncate">
                    {t(tmpl.descKey)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
